import gsap from "gsap";
import Fuse from "fuse.js";
import { AnimatePresence, motion } from "motion/react";
import className from "classnames/bind";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Container, Button } from "@components";
import { getLinkData, removeFilterStyles, sortInductees } from "@/core/utils";
import ScrollTrigger from "gsap/ScrollTrigger";
import SearchIcon from "@/assets/svg/search.svg";
import InducteesAToZItem from "./InducteesAToZItem";
import { GetAllInducteesQuery } from "@/__generated__/graphql";

import styles from "./InducteesAToZ.module.scss";
import { useGSAP } from "@gsap/react";
import { useLenis } from "lenis/react";
import { useHeader } from "@/providers/HeaderContext";
import { useViewSize } from "@/providers/ViewSizeContext";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import { useClientMount } from "@/providers/ClientMountProvider";

const cx = className.bind(styles);

interface InducteesAToZProps {
  inductees: GetAllInducteesQuery["allInductees"];
  searchPlaceholder: string;
  noResultsTitle: string;
  noResultsBody: string;
  noResultsCta: CTAData;
  onAnimateIn?: () => void;
}

const searchVariants = {
  active: {
    transform: "translate(-50%, 0%)",
    opacity: 1,
  },
  inactive: {
    transform: "translate(-50%, 100%)",
    opacity: 0,
  },
};

export default function InducteesAToZ({
  inductees,
  searchPlaceholder,
  noResultsTitle,
  noResultsBody,
  noResultsCta,
  onAnimateIn,
}: InducteesAToZProps) {
  const PAGE_SIZE = 24;
  const rootEl = useRef<HTMLDivElement>(null);
  const initialData = useMemo(
    () => sortInductees(inductees),
    [inductees]
  );
  const [data, setData] = useState(initialData);
  const cardRefs = useRef<HTMLElement[]>([]);
  const [results, setResults] = useState(data.slice(0, PAGE_SIZE));
  const inducteeGridEl = useRef<HTMLDivElement>(null);
  const alphabetButtonsEl = useRef<HTMLDivElement>(null);
  const searchInputEl = useRef<HTMLInputElement>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const { labels } = usePageWrapperData();
  const [showSearch, setShowSearch] = useState(false);
  const cardsPerRow = useRef(6);
  const [scrolling, setScrolling] = useState(false);
  const ignoreScroll = useRef(false);
  const triggerRef = useRef<ScrollTrigger | null>(null);
  const pageIndex = useRef(1);
  const fistRender = useRef(true);
  const ctaData = getLinkData(noResultsCta);
  const alphabet = Array.from({ length: 26 }, (_, i) =>
    String.fromCharCode(65 + i)
  );
  const lenis = useLenis();
  const lenisRef = useRef(lenis);
  const fuse = new Fuse(initialData, {
    keys: [
      "title",
      "inducteeLandingMembers.name",
      "inducteeData.category",
      "inducteeData.inductedBy",
      "inducteeData.inducteeClass.slug",
      "inducteeData.relatedSearchTerms.searchTerm",
    ],
    includeScore: true,
    threshold: 0.25,
  });
  const {
    isScrolled: isHeaderScrolled,
    scrollDirection: headerScrollDirection,
    menuExpanded,
  } = useHeader();
  const { isMobile, isTablet, isDesktop, viewWidth, isReady } = useViewSize();
  const { mounted } = useClientMount();
  const [activeLetter, setActiveLetter] = useState('');

  const setCardRefs = (
    el: { getInnerRef: () => HTMLAnchorElement | null } | null,
    index: number
  ) => {
    const innerRef = el?.getInnerRef ? el.getInnerRef() : null;
    if (innerRef) cardRefs.current[index] = innerRef;
    else delete cardRefs.current[index];
  };

  const createScrollTrigger = () => {
    if (!mounted || !isReady) return;
    gsap.registerPlugin(ScrollTrigger);
    gsap.registerPlugin(ScrollToPlugin);
    if (triggerRef.current) {
      triggerRef.current.kill();
      triggerRef.current = null;
    }
    if (data.length > PAGE_SIZE) {
      let loading = false;
      triggerRef.current = ScrollTrigger.create({
        trigger: rootEl.current,
        start: "bottom bottom+=300px",
        end: "bottom bottom",
        onUpdate: async (self) => {
          if (ignoreScroll.current) {
            ignoreScroll.current = false;
            return;
          }

          if (
            !loading &&
            self.direction > 0 &&
            self.end - self.scroll() <= 100
          ) {
            loading = true;
            const nextResults = data.slice(
              pageIndex.current * PAGE_SIZE,
              (pageIndex.current + 1) * PAGE_SIZE
            );
            if (nextResults.length > 0) {
              await setResults((prev) => [...prev, ...nextResults]);
              pageIndex.current += 1;
              triggerRef.current?.refresh();
            }
            loading = false;
          }
        },
      });
    }
  };

  const onButtonClick = async (letter: string, focusCard = false) => {
    setActiveLetter(letter);
    const index = data.findIndex((item) => {
      const title = item.inducteeData.sortName || item.title;
      return title.toUpperCase().startsWith(letter.toUpperCase());
    });
    if (index !== -1) {
      let delay = 0;
      const totalPages = Math.ceil(data.length / PAGE_SIZE);
      const neededPage = Math.floor(index / PAGE_SIZE) + 1;
      const pageNeeded = Math.min(neededPage + 1, totalPages);
      if (pageNeeded > pageIndex.current) {
        setScrolling(true);
        await setResults((prev) => [
          ...prev,
          ...data.slice(pageIndex.current * PAGE_SIZE, pageNeeded * PAGE_SIZE),
        ]);
        pageIndex.current = pageNeeded;
        delay = 50;
      }
      setTimeout(() => {
        if (!mounted || !isReady) return;
        setScrolling(true);
        if (cardRefs.current[index]) {
          const { top } = cardRefs.current[index].getBoundingClientRect();
          gsap.to(window, {
            scrollTo: {
              y: top + window.scrollY - 121,
            },
            duration: 0.75,
            ease: "power2.inOut",
            onComplete: () => {
              setScrolling(false);
              if (focusCard) {
                cardRefs.current[index]?.focus();
              }
            },
          });
        }
      }, delay);
    }
  };

  const handleGridKeyDown = (event: KeyboardEvent) => {
    const { key } = event;
    if (key === "ArrowDown") {
      event.preventDefault();
      const currentIndex = cardRefs.current.findIndex(
        (el) => el === document.activeElement
      );
      const totalCards = cardRefs.current.length - 1;
      if (currentIndex !== -1) {
        const nextIndex = currentIndex + cardsPerRow.current;
        if (nextIndex < cardRefs.current.length) {
          cardRefs.current[nextIndex]?.focus();
        } else if (
          nextIndex > cardRefs.current.length &&
          currentIndex < totalCards
        ) {
          cardRefs.current[cardRefs.current.length - 1]?.focus();
        } else if (currentIndex === totalCards) {
          const firstCard = cardRefs.current[0];
          firstCard?.focus();
        }
      }
    } else if (key === "ArrowUp") {
      event.preventDefault();
      const currentIndex = cardRefs.current.findIndex(
        (el) => el === document.activeElement
      );
      if (currentIndex !== -1) {
        const prevIndex = currentIndex - cardsPerRow.current;
        if (prevIndex >= 0) {
          cardRefs.current[prevIndex]?.focus();
        } else if (prevIndex < 0 && currentIndex > 0) {
          cardRefs.current[0]?.focus();
        }
      }
    } else if (key === "ArrowLeft") {
      event.preventDefault();
      const currentIndex = cardRefs.current.findIndex(
        (el) => el === document.activeElement
      );
      if (currentIndex !== -1) {
        const prevIndex = currentIndex - 1;
        if (prevIndex >= 0) {
          cardRefs.current[prevIndex]?.focus();
        }
      }
    } else if (key === "ArrowRight") {
      event.preventDefault();
      const currentIndex = cardRefs.current.findIndex(
        (el) => el === document.activeElement
      );
      if (currentIndex !== -1) {
        const nextIndex = currentIndex + 1;
        if (nextIndex < cardRefs.current.length) {
          cardRefs.current[nextIndex]?.focus();
        }
      }
    } else if (event.key === "Tab") {
      event.preventDefault();
      if (event.shiftKey) {
        searchInputEl.current?.focus();
      } else {
        const footer = document.querySelector("footer");
        const nextFocusable = footer?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;

        if (nextFocusable) {
          nextFocusable?.focus();
          nextFocusable.scrollIntoView();
        }
      }
    }
  };

  const handleAlphabetListKeyDown = (event: KeyboardEvent) => {
    const { key } = event;
    const children = Array.from(alphabetButtonsEl.current?.children || []);
    if (key === "ArrowDown") {
      event.preventDefault();
      const currentIndex = children.findIndex(
        (el) => el === document.activeElement
      );
      if (currentIndex !== -1) {
        const nextIndex = currentIndex + 1;
        if (nextIndex < children.length) {
          (children[nextIndex] as HTMLButtonElement)?.focus();
        }
      }
    } else if (key === "ArrowUp") {
      event.preventDefault();
      const currentIndex = children.findIndex(
        (el) => el === document.activeElement
      );
      if (currentIndex !== -1) {
        const prevIndex = currentIndex - 1;
        if (prevIndex >= 0) {
          (children[prevIndex] as HTMLButtonElement)?.focus();
        }
      }
    }
  };

  const handleSearchKeydown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Tab") {
      event.preventDefault();
      if (event.shiftKey) {
        const firstAlphabetButton = alphabetButtonsEl.current
          ?.firstElementChild as HTMLButtonElement;
        firstAlphabetButton?.focus();
      } else {
        const firstCard = cardRefs.current[0];
        firstCard?.focus();
      }
    } else if (event.key === "Enter") {
      searchInputEl?.current.blur();
    }
  };

  const handleFocusInput = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (isMobile || isTablet) {
        setIsSearchFocused(true);
        window.scrollTo({ top: 0 });
        gsap.delayedCall(0.4, () => {
          window.scrollTo({ top: 0 });
        });
      } else {
        setIsSearchFocused(true);
      }
    },
    [setIsSearchFocused]
  );

  const handleBlurInput = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsSearchFocused(false);
  };

  const handleAlphabetButtonKeydown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    letter: string
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsSearchFocused(false);
      onButtonClick(letter, true);
    }
  };

  const onAnimationComplete = async (latest?: any) => {
    const main = document.querySelector<HTMLElement>("main");
    await removeFilterStyles(rootEl.current);
    await removeFilterStyles(main);
    // Don't set showSearch here anymore - handled by useEffect timer
  };

  const onAnimationStart = async (latest: any) => {
    gsap.to(inducteeGridEl.current, {
      x: -50,
      ease: "power3.in",
      opacity: 0,
      duration: 0.5,
    });
    gsap.killTweensOf(alphabetButtonsEl.current.children);
    gsap.to(alphabetButtonsEl.current.children, {
      x: -60,
      opacity: 0,
      ease: "power3.in",
      stagger: 0.01,
      duration: 0.5,
    });
  };

  const filterInductees = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value.trim().toLowerCase();
    if (searchTerm === "") {
      setData(initialData);
      pageIndex.current = 1;
      createScrollTrigger();
      return;
    }
    lenisRef.current?.scrollTo(0, { immediate: true });
    const filteredResults = fuse
      .search(searchTerm)
      .map((result) => result.item);
    setData(filteredResults);
    pageIndex.current = 1;
    ignoreScroll.current = true;
    createScrollTrigger();
    setScrolling(false);
  };

  useEffect(() => {
    inducteeGridEl.current?.addEventListener("keydown", handleGridKeyDown);
    alphabetButtonsEl.current?.addEventListener(
      "keydown",
      handleAlphabetListKeyDown
    );
    return () => {
      inducteeGridEl.current?.removeEventListener("keydown", handleGridKeyDown);
      alphabetButtonsEl.current?.removeEventListener(
        "keydown",
        handleAlphabetListKeyDown
      );
    };
  }, [inducteeGridEl]);

  useGSAP(() => {
    if (!mounted || !isReady || !inducteeGridEl.current) {
      return;
    }

    window.scrollTo(0, 0);
    lenis?.scrollTo(0, { immediate: true });
    lenis?.stop();
    document.body.style.overflow = "hidden";
    lenisRef.current?.stop();
    lenisRef.current?.scrollTo(0);

    gsap.fromTo(inducteeGridEl.current, { opacity: 0 }, { opacity: 1 });

    gsap.fromTo(
      alphabetButtonsEl.current.children,
      {
        y: 90,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        ease: "power3.out",
        stagger: 0.02,
        duration: 1,
        delay: 0.4,
      }
    );

    let columns = 2;
    let scrollTriggerEnd = "5% 10%";
    if (isTablet) {
      columns = 4;
      scrollTriggerEnd = "20% 20%";
    } else if (isDesktop) {
      columns = 6;
      scrollTriggerEnd = "50% 100%";
    }

    Array.from(inducteeGridEl.current.children).forEach((el, index) => {
      let ladderY = -(index % columns) * 25;
      const finalY = ladderY + (index % 2) * 40;

      gsap.killTweensOf(el);

      gsap.fromTo(
        el,
        {
          y: fistRender.current
            ? `${finalY + 50 + (index / columns) * 40}%`
            : `${finalY}%`,
        },
        {
          y: `${finalY}%`,
          ease: "power4.out",
          duration: fistRender.current ? 1 : 0.5,
          delay: (index % columns) * 0.07,
          onComplete: () => {
            lenis.scrollTo(0, {
              immediate: true,
            });

            gsap.to(el, {
              y: 0,
              scrollTrigger: {
                scrub: 1,
                trigger: document.body,
                start: "0% 0%",
                end: scrollTriggerEnd,
              },
            });

            if (index === columns - 1) {
              onAnimateIn?.();
            }

            document.body.style.overflow = "";
            fistRender.current = false;
          },
        }
      );

      gsap.fromTo(
        el,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          ease: "linear",
          duration: 0.4,
          delay: fistRender.current ? (index % columns) * 0.1 : 0,
        }
      );

      if (fistRender.current) {
        gsap.fromTo(
          alphabetButtonsEl.current.children,
          {
            y: 90,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            ease: "power3.out",
            stagger: 0.02,
            duration: 1,
            delay: 0.4,
          }
        );
      }
    });

    return () => {
      document.body.style.overflow = "";
    };
  }, [
    mounted,
    isReady,
    lenis,
    inducteeGridEl.current,
    alphabetButtonsEl.current,
    isDesktop,
    isTablet,
    isMobile,
    viewWidth,
  ]);

  useEffect(() => {
    if (triggerRef.current) {
      triggerRef.current.refresh();
    }
  }, [results]);

  useEffect(() => {
    lenisRef.current = lenis;
  }, [lenis]);

  useEffect(() => {
    if (!mounted || !isReady) return;
    createScrollTrigger();
    setResults(data.slice(0, PAGE_SIZE));
    return () => {
      if (triggerRef.current) {
        triggerRef.current.kill();
        triggerRef.current = null;
      }
    };
  }, [mounted, isReady, data]);

  // Show search bar after grid animations complete
  useEffect(() => {
    // Wait for the grid animation to complete before showing search
    const timer = setTimeout(() => {
      setShowSearch(true);
    }, 1500); // Match the grid animation duration

    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  useEffect(() => {
    if (menuExpanded) {
      lenisRef.current?.stop();
    } else {
      lenisRef.current?.start();
    }
  }, [menuExpanded]);

  return (
    <motion.div
      className={cx(["inductees-a-to-z"])}
      ref={rootEl}
      onAnimationComplete={onAnimationComplete}
      onAnimationStart={onAnimationStart}
      exit={{ left: 0.5 }}
      transition={{ ease: "easeIn", duration: 0.3 }}
    >
      <Container className={cx("container")}>
        <nav
          className={cx("alphabet-buttons", {
            "full-size":
              isHeaderScrolled &&
              headerScrollDirection === "down" &&
              !isMobile &&
              !isTablet,
          })}
          ref={alphabetButtonsEl}
          aria-label={labels?.alphabeticalJumpLinks}
        >
          {alphabet.map((letter, index) => (
            <button
              className={cx("alphabet-button", "LG-EyebrowTag", { 'is-active': activeLetter === letter})}
              key={letter}
              onClick={() => onButtonClick(letter)}
              onKeyDown={(e) => handleAlphabetButtonKeydown(e, letter)}
              tabIndex={index > 0 ? -1 : 0}
              aria-current={activeLetter === letter ? 'true' : undefined}
            >
              {letter}
            </button>
          ))}
        </nav>
        <div
          className={cx("search-bar-bg", {
            "search-bar-bg--focused": isSearchFocused && !isDesktop,
          })}
        />
        <motion.div
          className={cx("search-bar", {
            "search-bar--focused": isSearchFocused && !isDesktop,
            "search-bar--focused-desktop": isSearchFocused && isDesktop,
          })}
          variants={searchVariants}
          initial="inactive"
          animate={showSearch ? "active" : "inactive"}
        >
          <SearchIcon className={cx("search-icon")} />
          <input
            type="text"
            placeholder={searchPlaceholder}
            className={cx("search-input")}
            onInput={filterInductees}
            onKeyDown={handleSearchKeydown}
            onFocus={handleFocusInput}
            onBlur={handleBlurInput}
            onSubmit={handleBlurInput}
            ref={searchInputEl}
          />
        </motion.div>
        <div className={cx("sr-only")} aria-live="polite" aria-label="Search results">{ results.length + " " + "Search results" }</div>
        <div className={cx("inductee-grid")} ref={inducteeGridEl} aria-label="Search results" role="listbox">
          {results.map((inductee, index) => (
            <InducteesAToZItem
              key={`${inductee.title}-${index}`}
              ref={(el) => setCardRefs(el, index)}
              {...inductee}
            />
          ))}
        </div>

        <AnimatePresence>
          {results.length === 0 && (
            <motion.div
              className={cx("no-results-container")}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <h2 className={cx("title-lc-xs")}>{noResultsTitle}</h2>
              <p className={cx("text-m", "text-center")}>
                {noResultsBody}
              </p>
              { ctaData && <Button {...ctaData} /> }
            </motion.div>
          )}
        </AnimatePresence>
      </Container>
    </motion.div>
  );
}
